import { useState } from "react";
import { Link } from "react-router-dom";

import { useCriarEmpresa, useEmpresas } from "@/hooks/useApi";

function formatCnpj(cnpj: string): string {
  if (cnpj.length !== 14) return cnpj;
  return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(12)}`;
}

export function EmpresasPage() {
  const [q, setQ] = useState("");
  const { data, isLoading } = useEmpresas({ q });
  const criar = useCriarEmpresa();
  const [form, setForm] = useState({
    razao_social: "",
    cnpj: "",
    cidade: "",
    uf: "SP",
    cnae: "",
    grau_risco: "",
  });

  const cnpjDigitos = form.cnpj.replace(/\D/g, "");
  const ok = form.razao_social.trim().length >= 2 && cnpjDigitos.length === 14;

  async function handleSubmit() {
    if (!ok) return;
    try {
      await criar.mutateAsync({
        razao_social: form.razao_social.trim(),
        cnpj: cnpjDigitos,
        cidade: form.cidade.trim() || null,
        uf: form.uf.trim() || null,
        cnae: form.cnae.trim() || null,
        grau_risco: form.grau_risco ? Number(form.grau_risco) : null,
      });
      setForm({ razao_social: "", cnpj: "", cidade: "", uf: "SP", cnae: "", grau_risco: "" });
    } catch { /* erro via criar.error */ }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-semibold">Empresas</h2>
        <p className="text-sm text-ink-muted">Empresas clientes da medicina ocupacional.</p>
      </div>

      <div className="card p-4">
        <h3 className="font-display text-base font-semibold">Nova empresa</h3>
        <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
          <input className="input col-span-2" placeholder="razão social" value={form.razao_social}
            onChange={(e) => setForm({ ...form, razao_social: e.target.value })} />
          <input className="input" placeholder="CNPJ" inputMode="numeric" value={form.cnpj}
            onChange={(e) => setForm({ ...form, cnpj: e.target.value })} />
          <input className="input" placeholder="CNAE (opc.)" value={form.cnae}
            onChange={(e) => setForm({ ...form, cnae: e.target.value })} />
          <input className="input" placeholder="cidade" value={form.cidade}
            onChange={(e) => setForm({ ...form, cidade: e.target.value })} />
          <input className="input" placeholder="UF" maxLength={2} value={form.uf}
            onChange={(e) => setForm({ ...form, uf: e.target.value.toUpperCase() })} />
          <select className="input" value={form.grau_risco}
            onChange={(e) => setForm({ ...form, grau_risco: e.target.value })}>
            <option value="">grau de risco (opc.)</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
          </select>
        </div>
        {form.cnpj && cnpjDigitos.length !== 14 ? (
          <p className="mt-1 text-xs text-anarosa">CNPJ precisa de 14 dígitos ({cnpjDigitos.length}/14)</p>
        ) : null}
        <div className="mt-3">
          <button className="btn-primary" disabled={!ok || criar.isPending} onClick={handleSubmit}>
            {criar.isPending ? "Salvando…" : "Adicionar"}
          </button>
        </div>
        {criar.error ? (
          <p className="mt-2 text-sm text-anarosa">{(criar.error as Error).message}</p>
        ) : null}
      </div>

      <input
        className="input max-w-md"
        placeholder="Buscar por razão social ou CNPJ…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />

      {isLoading ? <p className="text-ink-muted">Carregando…</p> : null}
      {data && data.length > 0 ? (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-4 py-3">Razão social</th>
                <th className="px-4 py-3">CNPJ</th>
                <th className="px-4 py-3">Cidade/UF</th>
                <th className="px-4 py-3">Grau de risco</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.map((e) => (
                <tr key={e.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2">
                    <Link className="font-medium text-anarosa hover:underline" to={`/empresas/${e.id}`}>
                      {e.razao_social}
                    </Link>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{formatCnpj(e.cnpj)}</td>
                  <td className="px-4 py-2 text-ink-muted">
                    {e.cidade ?? "—"}{e.uf ? `/${e.uf}` : ""}
                  </td>
                  <td className="px-4 py-2 text-ink-muted">{e.grau_risco ?? "—"}</td>
                  <td className="px-4 py-2 text-right">
                    <Link className="btn-secondary px-2 py-1 text-xs" to={`/empresas/${e.id}`}>
                      Unidades / setores / funções
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : !isLoading ? (
        <p className="text-sm text-ink-muted">Nenhuma empresa encontrada.</p>
      ) : null}
    </div>
  );
}
